import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CLASSES = [
  { text: "AIS NEG // DARK VESSEL", hot: true },
  { text: "AIS NEG // DARK VESSEL", hot: true },
  { text: "AIS OK // FISHING VSL", hot: false },
  { text: "AIS OK // CARGO", hot: false },
  { text: "MODE-S NEG // UNK AIR", hot: true },
  { text: "ESM HIT // RF EMITTER", hot: true },
  { text: "NO SIG // DRIFT CONTACT", hot: false },
  { text: "IR HIT // SMALL CRAFT", hot: false },
];

const SWEEP_MS = 7000; // matches .radar-sweep animation duration

export default function RadarTargets() {
  const [blips, setBlips] = useState([]);
  const startRef = useRef(Date.now());
  const spawnRef = useRef(0);
  const idRef = useRef(0);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const sweep = (((now - startRef.current) / SWEEP_MS) * 360) % 360;
      let newBlip = null;
      if (now - spawnRef.current > 2600) {
        spawnRef.current = now;
        // keep contacts in the upper arc of the scope, clear of the headline/text below
        let x = 50, y = 50;
        for (let tries = 0; tries < 40; tries++) {
          const r = 16 + Math.random() * 30;
          const phi = Math.random() * Math.PI * 2;
          const cx = 50 + r * Math.cos(phi);
          const cy = 50 + r * Math.sin(phi);
          if (cy >= 36 && cy <= 54) { x = cx; y = cy; break; }
          if (tries === 39) { x = 50; y = 42; }
        }
        const angle = (Math.atan2(x - 50, -(y - 50)) * 180 / Math.PI + 360) % 360;
        const cls = CLASSES[Math.floor(Math.random() * CLASSES.length)];
        newBlip = {
          id: ++idRef.current,
          name: `CTC-${100 + Math.floor(Math.random() * 900)}`,
          cls, x, y, angle, status: "pending", detectedAt: 0,
        };
      }
      setBlips((prev) => {
        let next = prev.map((b) => {
          if (b.status === "pending") {
            const diff = (sweep - b.angle + 360) % 360;
            if (diff < 12) return { ...b, status: "live", detectedAt: now };
          }
          if (b.status === "live" && now - b.detectedAt > 6500) {
            return { ...b, status: "fade" };
          }
          return b;
        });
        next = next.filter((b) => b.status !== "fade" || now - b.detectedAt < 8000);
        if (newBlip && next.filter((b) => b.status !== "fade").length < 4) {
          next = [...next, newBlip];
        }
        return next;
      });
    };
    const id = setInterval(tick, 120);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="hero-radar-targets" className="absolute inset-0 hidden lg:block">
      <AnimatePresence>
        {blips.map((b) => (
          b.status === "pending" ? null : (
          <motion.div
            key={b.id}
            data-testid={`radar-blip-${b.id}`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: b.status === "fade" ? 0 : 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute"
            style={{ left: `${b.x}%`, top: `${b.y}%` }}
          >
            {(
              <>
                <span className="animate-ping absolute -left-1 -top-1 h-2 w-2 rounded-full bg-orange-500" />
                <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-orange-400 shadow-[0_0_8px_rgba(249,115,22,0.9)]" />
                <div
                  className={`absolute top-2 whitespace-nowrap border px-1.5 py-1 font-body text-[8px] leading-tight tracking-widest sm:text-[9px] ${
                    b.x > 60 ? "right-2.5" : "left-2.5"
                  } ${
                    b.cls.hot
                      ? "border-orange-500/60 bg-black/80 text-orange-400"
                      : "border-stone-600/60 bg-black/70 text-stone-400"
                  }`}
                >
                  <div className="font-bold">{b.name} // TRK</div>
                  <div>{b.cls.text}</div>
                </div>
              </>
            )}
          </motion.div>
          )
        ))}
      </AnimatePresence>
    </div>
  );
}
